import { useNavigate } from 'react-router-dom';
import { BarChart3, ShoppingCart, Building, Scale, ArrowLeft, Sparkles, CheckCircle2, TrendingUp, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin, isCompanyAdmin } = useAuth();

  // Check if user has permission
  const hasPermission = (permission) => {
    if (!user) return false;
    if (isSuperAdmin || isCompanyAdmin) return true;
    const permissions = user.permissions || [];
    return permissions.includes('all') || permissions.includes(permission);
  };

  const allApps = [
    {
      id: 'buildings',
      title: 'كميات العمائر',
      description: 'نظام إدارة كميات العمائر السكنية الكبيرة',
      icon: Building,
      color: 'from-violet-500 to-purple-600',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      path: '/buildings',
      permission: 'buildings',
      features: ['نماذج الشقق', 'حساب الكميات', 'تتبع التوريد', 'تصدير BOQ']
    },
    {
      id: 'analysis',
      title: 'تحليل الأسعار',
      description: 'تحليل فروقات الأسعار للأصناف المكررة',
      icon: TrendingUp,
      color: 'from-orange-500 to-red-600',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      path: '/analysis',
      permission: 'analysis',
      features: ['رفع ملفات Excel/PDF', 'كشف فروقات الأسعار', 'تقارير تفصيلية', 'تصدير Excel/PDF']
    },
    {
      id: 'compare',
      title: 'مقارنة المشاريع',
      description: 'قارن بين مشاريعك وتتبع الأسعار والكميات',
      icon: BarChart3,
      color: 'from-emerald-500 to-teal-600',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      path: '/compare',
      permission: 'compare',
      features: ['رفع ملفات Excel', 'مقارنة الأسعار', 'تقارير مفصلة', 'كشف التكرارات']
    },
    {
      id: 'procurement',
      title: 'إدارة المشتريات',
      description: 'إدارة عروض الأسعار والموردين والتقارير',
      icon: ShoppingCart,
      color: 'from-amber-500 to-orange-600',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      path: '/procurement',
      permission: 'procurement',
      features: ['إدارة الموردين', 'عروض الأسعار', 'تقارير المقارنة', 'إدارة المشاريع']
    },
    {
      id: 'balance',
      title: 'ميزان المراجعة',
      description: 'قارن موازين المراجعة واحصل على تقارير تحليلية',
      icon: Scale,
      color: 'from-blue-500 to-indigo-600',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      path: '/balance',
      permission: 'balance',
      features: ['رفع ملفات Excel', 'مقارنة تفصيلية', 'حساب الفروقات', 'تصدير Excel/PDF']
    },
    {
      id: 'system',
      title: 'إعدادات النظام',
      description: 'النسخ الاحتياطي والصيانة وإدارة النظام',
      icon: Settings,
      color: 'from-gray-500 to-gray-700',
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
      path: '/system',
      permission: 'system',
      features: ['النسخ الاحتياطي', 'الصيانة', 'إحصائيات النظام', 'تصدير البيانات']
    }
  ];

  // Filter apps based on user permissions
  const apps = allApps.filter(app => hasPermission(app.permission));
  const appsCount = apps.length;

  return (
    <div className="min-h-screen pb-6">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10 mb-5 sm:mb-6 shadow-xl overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />
        
        <div className="relative max-w-4xl">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-300" />
            <span className="text-xs sm:text-sm text-blue-200 font-medium">منصة متكاملة</span>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 leading-tight">
            نظام إدارة المشاريع الموحد
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-blue-100 mb-4 sm:mb-5 leading-relaxed">
            منصة شاملة تجمع {appsCount} تطبيقات متاحة لك لإدارة مشاريعك بكفاءة
          </p>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {[`${appsCount} تطبيقات`, 'قاعدة موحدة', 'سهل الاستخدام'].map((text, idx) => (
              <div key={idx} className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full">
                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-300" />
                <span className="text-xs sm:text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Apps Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
        {apps.map((app) => {
          const Icon = app.icon;
          return (
            <div
              key={app.id}
              className="bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer active:scale-[0.98]"
              onClick={() => navigate(app.path)}
            >
              {/* Header with gradient */}
              <div className={`bg-gradient-to-br ${app.color} p-4 sm:p-5 text-white relative overflow-hidden`}>
                <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full translate-x-1/2 -translate-y-1/2" />
                <div className="flex items-start justify-between mb-3 relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <ArrowLeft size={20} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-1">{app.title}</h3>
                <p className="text-white/80 text-xs sm:text-sm line-clamp-2">{app.description}</p>
              </div>

              {/* Features List */}
              <div className="p-3 sm:p-4">
                <ul className="space-y-1.5 sm:space-y-2">
                  {app.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-600 text-xs sm:text-sm">
                      <div className={`w-1.5 h-1.5 rounded-full ${app.iconBg.replace('bg-', 'bg-')}`} 
                           style={{backgroundColor: app.iconColor.includes('emerald') ? '#10b981' : 
                                                   app.iconColor.includes('violet') ? '#8b5cf6' :
                                                   app.iconColor.includes('amber') ? '#f59e0b' : '#3b82f6'}} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Button */}
              <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                <button className={`w-full bg-gradient-to-r ${app.color} text-white font-semibold py-2.5 sm:py-3 rounded-xl text-sm sm:text-base transition-all duration-200 shadow-sm hover:shadow-md`}>
                  الدخول
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Stats Section */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-md p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 text-center">
          إحصائيات النظام
        </h2>
        <div className="grid grid-cols-4 gap-2 sm:gap-4">
          {[
            { value: appsCount.toString(), label: 'تطبيقات', color: 'text-blue-600', bg: 'bg-blue-50' },
            { value: '∞', label: 'مشاريع', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { value: '100%', label: 'أمان', color: 'text-violet-600', bg: 'bg-violet-50' },
            { value: '24/7', label: 'متاح', color: 'text-amber-600', bg: 'bg-amber-50' },
          ].map((stat, idx) => (
            <div key={idx} className={`${stat.bg} rounded-xl p-2 sm:p-4 text-center`}>
              <div className={`text-xl sm:text-2xl lg:text-3xl font-bold ${stat.color} mb-0.5 sm:mb-1`}>{stat.value}</div>
              <div className="text-gray-600 text-[10px] sm:text-xs">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
